/**
 * The moving background of the sign-up story: a twisting band of fine lines, like silk
 * or a long exposure of a voice, that swells while people speak. Plain WebGL, so the
 * login page carries no 3D library. Without WebGL the panel's CSS background stands in.
 *
 * Doctors' laptops are often old and clinics often work over remote desktop, so it is
 * careful with the machine: software rendering gets one still frame, and real hardware
 * is timed at the start and watched as it runs. It lowers the resolution when it falls
 * behind, and holds still if that is not enough. Timing the frame loop alone would miss
 * this, because a slow GPU never shows up as a slow main thread.
 */

const VERTEX = `attribute vec2 a; void main() { gl_Position = vec4(a, 0.0, 1.0); }`

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 uRes;
uniform float uTime;
uniform float uEnergy;
uniform float uCenter;
uniform float uBand;

float wave(float x, float t, float s) {
  return sin(x * 1.4 + t * 0.31 + s)
    + 0.45 * sin(x * 2.9 - t * 0.23 + s * 1.6)
    + 0.18 * sin(x * 6.1 + t * 0.52 + s * 2.7);
}

float grain(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  vec2 p = vec2((uv.x - 0.5) * uRes.x / uRes.y, uv.y - uCenter);

  vec3 col = mix(vec3(0.012, 0.031, 0.082), vec3(0.024, 0.063, 0.165), uv.y);
  float reach = abs(p.y);
  col += vec3(0.04, 0.13, 0.38) * exp(-pow(p.y / (uBand * 2.6), 2.0)) * (0.30 + 0.30 * uEnergy);

  // uBand is how far the ribbon may stray at full voice, so it stays in the gap between
  // the texts and they stay readable, whatever the height of the panel.
  float limit = uBand * 2.4;
  if (reach < limit) {
    float open = 0.62 + 0.38 * uEnergy;
    float voice = uBand * 0.14 * open;
    float spread = uBand * 1.5 * open;
    float weight = uBand * 0.0058;
    float core = uBand * 0.0098;
    vec3 sum = vec3(0.0);
    for (int i = 0; i < 34; i++) {
      float f = float(i) / 33.0;
      // Every line shares the voice, and the band twists through itself like silk.
      float twist = sin(p.x * 1.15 + uTime * 0.17 + f * 0.9);
      float y = voice * wave(p.x * 1.2, uTime, f * 0.8)
        + (f - 0.5) * spread * twist
        + uBand * 0.05 * sin(p.x * 4.0 + uTime * 0.4 + f * 9.0) * (0.4 + uEnergy);
      float line = weight / (abs(p.y - y) + core);
      vec3 tint = mix(vec3(0.09, 0.36, 0.93), vec3(0.62, 0.77, 1.0), f);
      sum += tint * line * (0.35 + 0.65 * sin(f * 3.14159));
    }
    sum *= smoothstep(limit, uBand * 1.15, reach);
    // A soft shoulder: where the band pinches it glows instead of clipping to white.
    col += (1.0 - exp(-sum * 0.62)) * vec3(0.86, 0.92, 1.0);
  }

  // A little grain, or the slow gradient shows bands on older screens.
  col += (grain(gl_FragCoord.xy + fract(uTime)) - 0.5) * 0.006;
  gl_FragColor = vec4(col, 1.0);
}`

/** At the start: what one frame may cost the GPU, leaving room to hold 30 frames a second. */
const TOO_SLOW_TO_DRAW_MS = 28
/** While running: draws are 30 a second, so this is well behind. */
const SLOW_FRAME_MS = 55
const FRAMES_TO_JUDGE = 30
const LOWEST_QUALITY = 0.45

const isSoftwareRendered = (gl: WebGLRenderingContext) => {
  const info = gl.getExtension('WEBGL_debug_renderer_info')
  const name = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : ''
  return /swiftshader|llvmpipe|software|basic render|warp/i.test(name)
}

export interface VoiceRibbon {
  /** Both as fractions of the canvas height: where it runs, from the bottom, and how far it may stray. */
  place: (center: number, band: number) => void
  /** Draws the first frame and settles on what this machine can afford. */
  prepare: (time: number, energy: number) => void
  /** Draws a frame, unless this machine cannot afford it. `force` always draws: a resize clears the canvas. */
  draw: (time: number, energy: number, force?: boolean) => void
  /** Call when the loop starts after a pause: that gap was not the machine being slow. */
  restartClock: () => void
  resize: () => void
  destroy: () => void
}

export const createVoiceRibbon = (canvas: HTMLCanvasElement): VoiceRibbon | null => {
  let gl: WebGLRenderingContext | null = null
  try {
    gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false, powerPreference: 'low-power' })
  } catch {
    return null
  }
  if (!gl) return null
  const context = gl

  const compile = (type: number, source: string) => {
    const shader = context.createShader(type)
    if (!shader) return null
    context.shaderSource(shader, source)
    context.compileShader(shader)
    return context.getShaderParameter(shader, context.COMPILE_STATUS) ? shader : null
  }
  const vertex = compile(context.VERTEX_SHADER, VERTEX)
  const fragment = compile(context.FRAGMENT_SHADER, FRAGMENT)
  const program = context.createProgram()
  if (!vertex || !fragment || !program) return null
  context.attachShader(program, vertex)
  context.attachShader(program, fragment)
  context.linkProgram(program)
  if (!context.getProgramParameter(program, context.LINK_STATUS)) return null
  context.useProgram(program)

  const triangle = context.createBuffer()
  context.bindBuffer(context.ARRAY_BUFFER, triangle)
  context.bufferData(context.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), context.STATIC_DRAW)
  const position = context.getAttribLocation(program, 'a')
  context.enableVertexAttribArray(position)
  context.vertexAttribPointer(position, 2, context.FLOAT, false, 0, 0)

  const uRes = context.getUniformLocation(program, 'uRes')
  const uTime = context.getUniformLocation(program, 'uTime')
  const uEnergy = context.getUniformLocation(program, 'uEnergy')
  const uCenter = context.getUniformLocation(program, 'uCenter')
  const uBand = context.getUniformLocation(program, 'uBand')

  let quality = 1
  let held = false
  let lastDraw = 0
  let judged = 0
  let slow = 0

  const resize = () => {
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5) * quality
    const width = Math.max(1, Math.round(canvas.clientWidth * ratio))
    const height = Math.max(1, Math.round(canvas.clientHeight * ratio))
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    }
    context.viewport(0, 0, width, height)
    context.uniform2f(uRes, width, height)
  }

  const lowerQuality = () => {
    if (quality <= LOWEST_QUALITY) {
      held = true
      return
    }
    quality = Math.max(LOWEST_QUALITY, quality * 0.7)
    resize()
  }

  const paint = (time: number, energy: number) => {
    context.uniform1f(uTime, time)
    context.uniform1f(uEnergy, energy)
    context.drawArrays(context.TRIANGLES, 0, 3)
  }

  const watchFrameRate = () => {
    const now = performance.now()
    if (lastDraw && now - lastDraw > SLOW_FRAME_MS) slow += 1
    if (lastDraw) judged += 1
    lastDraw = now
    if (judged < FRAMES_TO_JUDGE) return
    if (slow > judged / 2) lowerQuality()
    judged = 0
    slow = 0
  }

  resize()

  return {
    place: (center, band) => {
      context.uniform1f(uCenter, center)
      context.uniform1f(uBand, band)
    },
    prepare: (time, energy) => {
      if (isSoftwareRendered(context)) {
        held = true
      } else {
        // readPixels waits for the GPU, which is the cost the frame loop cannot see.
        const pixel = new Uint8Array(4)
        const frameCost = () => {
          const start = performance.now()
          paint(time, energy)
          context.readPixels(0, 0, 1, 1, context.RGBA, context.UNSIGNED_BYTE, pixel)
          return performance.now() - start
        }
        frameCost() // The first includes finishing the shader.
        while (!held && frameCost() > TOO_SLOW_TO_DRAW_MS) lowerQuality()
      }
      paint(time, energy)
    },
    draw: (time, energy, force = false) => {
      if (context.isContextLost()) return
      if (force) {
        paint(time, energy)
        return
      }
      if (held) return
      watchFrameRate()
      paint(time, energy)
    },
    restartClock: () => {
      lastDraw = 0
      judged = 0
      slow = 0
    },
    resize,
    destroy: () => {
      context.deleteProgram(program)
      context.deleteShader(vertex)
      context.deleteShader(fragment)
      context.deleteBuffer(triangle)
    },
  }
}
